import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { TagGreenIcon, TagOrangeIcon, TagRedIcon } from '../../../assets/icons';
import { formatAmount, formatMiles } from '../../../utils';
import { DashboardService } from '../../../services';

const colors = {
  orange: ['#F67B00','#FEAA57','#FFDB65'],
  red: ['#FF0000','#FF6C6C','#FFAD8F'],
  green: ['#16DB62','#33FF82','#AEFFCD'],
  blue: ['#396AFF','#7EB2FF','#89CEFF'],
}

const Home = () => {
  const { chart1, chart2, chart3, chart4, currentMonth, data } = useData();

  return (
    <main className="grid grid-cols-2 gap-6 items-start">
      <section className="bg-white shadow-md rounded-2xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <Link
            to="/billing/owner"
            className="flex items-center bg-[#FFD8D8] border border-red px-4 py-0.5 rounded-full"
          >
            <img className="w-4 h-4 mr-2" src={TagRedIcon} alt="" />
            <span className="text-lg font-bold text-[#FF5858]">Facturación total</span>
          </Link>
          <span className="text-xl text-red ml-4">{ currentMonth }</span>
        </div>

        <div className="flex items-center">
          <p className="whitespace-pre-wrap leading-4 font-semibold">Total{'\n'}facturación</p>
          <p className="text-4xl font-bold font-sans mx-4">{ formatAmount(data.billing.total) }</p>
          <p className="text-3xl font-semibold font-sans text-default">{ formatAmount(data.billing.total_local, 'Bs ') }</p>
        </div>

        <div className="grid grid-cols-2 pt-4">
          <div>
            <ReactApexChart
              height={260}
              options={chart1.options}
              series={chart1.series}
              type="pie"
            />

            <div className="mt-4 flex items-center">
              <div className="w-3 h-3 rounded-full bg-orange mr-2" />
              <p className="font-bold">Facturación por propios</p>
            </div>
            <p className="text-default font-bold space-x-2">
              <span>{ formatAmount(data.billing.own_total, '$') }</span>
              <span className="text-sm font-normal">{ formatAmount(data.billing.own_total_local, 'Bs ') }</span>
            </p>

            <div className="mt-4 flex items-center">
              <div className="w-3 h-3 rounded-full bg-red mr-2" />
              <p className="font-bold">Facturación por afiliados</p>
            </div>
            <p className="text-default font-bold space-x-2">
              <span>{ formatAmount(data.billing.affiliates_total, '$') }</span>
              <span className="text-sm font-normal">{ formatAmount(data.billing.affiliates_total_local, 'Bs ') }</span>
            </p>
          </div>

          <div>
            <Link to="billing/client">
              <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
                <p className="font-bold text-xl">Facturación por cliente</p>

                {data.billing.series.clients.map((item, index) => (
                  <Chart1Item key={index} item={item} />
                ))}
              </div>
            </Link>

            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
              <p className="font-bold text-xl">Facturación por ruta</p>
              {data.billing.series.routes.map((item, index) => (
                <Chart1Item key={index} item={item} />
              ))}
            </div>

            <Link to="billing/driver">
              <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
                <p className="font-bold text-xl">Facturación por conductor</p>
                {data.billing.series.drivers.map((item, index) => (
                  <Chart1Item key={index} item={item} />
                ))}
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white shadow-md rounded-2xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center bg-[#E8FFF1] border border-green px-4 py-0.5 rounded-full">
            <img className="w-4 h-4 mr-2" src={TagGreenIcon} alt="" />
            <span className="text-lg font-bold text-green">Comisión</span>
          </div>
          <span className="text-xl text-red ml-4">{ currentMonth }</span>
        </div>

        <div className="flex items-center">
          <p className="whitespace-pre-wrap leading-4 font-semibold">Total{'\n'}comisión</p>
          <p className="text-4xl font-bold font-sans mx-4">{ formatAmount(data.commissions.total) }</p>
          <p className="text-3xl font-semibold font-sans text-default">{ formatAmount(data.commissions.total_local, 'Bs ') }</p>
        </div>

        <div className="grid grid-cols-2 pt-4">
          <div>
            <ReactApexChart
              height={260}
              options={chart2.options}
              series={chart2.series}
              type="bar"
            />

            <div className="mt-4 flex items-center">
              <div className="w-3 h-3 rounded-full bg-orange mr-2" />
              <p className="font-bold">Comisiones por propios</p>
            </div>
            <p className="text-default font-bold space-x-2">
              <span>{ formatAmount(data.commissions.own_total, '$') }</span>
              <span className="text-sm font-normal">{ formatAmount(data.commissions.own_total_local, 'Bs ') }</span>
            </p>

            <div className="mt-4 flex items-center">
              <div className="w-3 h-3 rounded-full bg-green mr-2" />
              <p className="font-bold">Comisiones por afiliados</p>
            </div>
            <p className="text-default font-bold space-x-2">
              <span>{ formatAmount(data.commissions.affiliates_total, '$') }</span>
              <span className="text-sm font-normal">{ formatAmount(data.commissions.affiliates_total_local, 'Bs ') }</span>
            </p>
          </div>

          <div>
            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
              <p className="font-bold text-xl">Comisión por cliente</p>

              {data.commissions.series.clients.map((item, index) => (
                <Chart2Item key={index} item={item} />
              ))}
            </div>

            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
              <p className="font-bold text-xl">Comisión por propietario</p>

              {data.commissions.series.owners.map((item, index) => (
                <Chart2Item key={index} item={item} />
              ))}
            </div>

            <Link to="report/billing/route">
              <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
                <p className="font-bold text-xl">Comisión por ruta</p>

                {data.commissions.series.routes.map((item, index) => (
                  <Chart2Item key={index} item={item} />
                ))}
              </div>
            </Link>

            <div className="bg-[#F5F7FA] p-2 rounded-lg mb-4">
              <p className="font-bold text-xl">Comisión por conductor</p>

              {data.commissions.series.drivers.map((item, index) => (
                <Chart2Item key={index} item={item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white shadow-md rounded-2xl p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center bg-[#FFEEDE] border border-orange px-4 py-0.5 rounded-full">
            <img className="w-4 h-4 mr-2" src={TagOrangeIcon} alt="" />
            <span className="text-lg font-bold text-orange">Gastos mantenimientos</span>
          </div>
          <span className="text-xl text-red ml-4">{ currentMonth }</span>
        </div>

        <div className="flex-col">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-orange rounded-lg text-white p-2">
              <p className="text-lg font-semibold">Total de gastos</p>
              <p className="text-4xl font-bold">{ formatAmount(data.expenses.total, '$') }</p>
              <p className="text-xl font-bold opacity-80">{ formatAmount(data.expenses.total_local, 'Bs ') }</p>
            </div>

            <div className="bg-[#F5F7FA] rounded-lg text-black p-2">
              <p className="text-lg font-semibold">Cantidad de fallas reportadas</p>
              <p className="text-4xl font-bold">{ data.expenses?.failures || 0 }</p>
            </div>
          </div>

          <div className="flex-1">
            <ReactApexChart
              options={chart3.options}
              series={chart3.series}
              type="bar"
              height={350}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-orange mb-1">Repuestos</p>

              {data.expenses.series?.parts?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.orange.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-red mb-1">Categorías</p>

              {data.expenses.series?.categories?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.red.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-green mb-1">Proveedor</p>

              {data.expenses.series?.providers?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.green.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>

            <div className="border-2 border-default rounded-lg p-2">
              <p className="text-lg font-black text-[#396AFF] mb-1">Unidad</p>

              {data.expenses.series?.units?.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: colors.blue.at(index) }} />
                  <p className="flex-1">{ item.label }</p>
                  <span className="text-default font-semibold">{ formatMiles(item.value, 0, '') }</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-2xl font-bold">Gasto de combustible</span>
            <span className="text-xl text-red ml-4">{ currentMonth }</span>
          </div>

          <div className="flex-1">
            <ReactApexChart
              options={chart4.options}
              series={chart4.series}
              type="line"
              height={350}
            />
          </div>
        </div>
      </section>
    </main>
  );
}

const Chart1Item = ({ item }) => (
  <div className="mt-2">
    <p className="font-bold text-[#FF5858] line-clamp-1">{ item.label }</p>
    <p className="text-default font-bold space-x-2">
      <span>{ formatAmount(item.total, '$') }</span>
      <span className="text-sm font-normal">{ formatAmount(item.total_local, 'Bs ') }</span>
    </p>
  </div>
)

const Chart2Item = ({ item }) => (
  <div className="mt-2">
    <p className="font-bold text-green line-clamp-1">{ item.label }</p>
    <p className="text-default font-bold space-x-2">
      <span>{ formatAmount(item.total, '$') }</span>
      <span className="text-sm font-normal">{ formatAmount(item.total_local, 'Bs ') }</span>
    </p>
  </div>
)

const useData = () => {
  const currentMonth = moment().format('MMMM');
  const [data, setData] = useState({
    billing: {
      total: 0,
      total_local: 0,
      own_total: 0,
      own_total_local: 0,
      affiliates_total: 0,
      affiliates_total_local: 0,
      series: {
        clients: [],
        routes: [],
        drivers: [],
      },
    },
    commissions: {
      total: 0,
      total_local: 0,
      own_total: 0,
      own_total_local: 0,
      affiliates_total: 0,
      affiliates_total_local: 0,
      series: {
        clients: [],
        owners: [],
        routes: [],
        drivers: [],
      },
    },
    expenses: {
      total: 0,
      total_local: 0,
      failures: 0,
      series: {
        parts: [],
        categories: [],
        providers: [],
        units: [],
        gas: [],
      },
    },
  });
  const [chart1, setChart1] = useState({
    series: [],
    options: {
      colors: ['#F67B00', '#FF0000'],
      labels: ['Facturación por propios', 'Facturación por afiliados'],
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '16px',
        },
      },
      legend: {
        show: false,
      },
    },
  });
  const [chart2, setChart2] = useState({
    series: [],
    options: {
      colors: ['#F67B00', '#16DB62'],
      labels: ['Comisiones por propios', 'Comisiones por afiliados'],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        zoom: false,
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 12,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      grid: {
        borderColor: '#e5e7eb',
        yaxis: {
          lines: { show: true }
        },
        xaxis: {
          lines: { show: false }
        }
      },
      yaxis: {
        tickAmount: 11,
        labels: {
          style: {
            colors: '#9ca3af',
            fontSize: '14px',
          }
        }
      },
      xaxis: {
        labels: {
          show: false,
        },
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: false
      },
    },
  });
  const [chart3, setChart3] = useState({
    series: [],
    options: {
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: {
          show: false
        }
      },
      dataLabels: {
        enabled: false
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '75%',
          borderRadius: 12,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      grid: {
        borderColor: '#e5e7eb',
        yaxis: {
          lines: { show: true }
        },
        xaxis: {
          lines: { show: false }
        }
      },
      yaxis: {
        tickAmount: 11,
        labels: {
          style: {
            colors: '#9ca3af',
            fontSize: '14px',
          }
        }
      },
      xaxis: {
        labels: {
          show: false,
        },
      },
      fill: {
        opacity: 1
      },
      legend: {
        show: false
      },
      tooltip: {
        custom: function (opts) {
          const w = opts.w;
          const serie = w.config.series[opts.seriesIndex];
          let serieName = '';
          let value = '';
          let color = '';
          let ylabel = '';

          const data = serie?.data?.at(opts.dataPointIndex);
          if (data) {
            serieName = data?.meta?.name || serie?.name || '';
            ylabel = data?.x;
            value = data?.y;
            color = data?.fillColor;
          } else {
            serieName = serie?.name || '';
          }

          return (
            '<div class="apexcharts-tooltip-rangebar">' +
              '<div class="flex items-center">'+
                '<div class="apexcharts-tooltip-marker" style="border-radius: 20px;background-color: ' + color + '"></div>' +
                '<span class="series-name pt-1">' +
                  ylabel +
                '</span>' +
              '</div>' +
              '<div> <span class="category">' +
                serieName +
              ': </span>' +
              '<span class="value start-value">' +
                value +
              '</span>' +
            '</div>'
          )
        },
      },
    },
  });
  const [chart4, setChart4] = useState({
    series: [
      {
        data: [],
      },
    ],
    options: {
      chart: {
        height: 350,
        type: 'line',
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
      },
      markers: {
        size: 0,
      },
      legend: {
        show: false,
      },
      fill: {
        colors: ['#F67B00'],
      },
      tooltip: {
        custom: function (opts) {
          const w = opts.w;
          const serie = w.config.series.at(0);
          const value = serie?.data?.at(opts.dataPointIndex) || '0';

          return (
            '<div class="apexcharts-tooltip-rangebar">' +
              '<span class="value start-value">' +
                formatMiles(value) +
              '</span>' +
            '</div>'
          )
        },
      },
    },
  });

  const fetchData = async () => {
    try {
      const response = await DashboardService.admin.findAll();
      setData(response);
      setChart1(s => ({
        ...s,
        series: [
          Number(response.billing?.own_total || 0),
          Number(response.billing?.affiliates_total || 0),
        ],
      }));

      setChart2(s => ({
        ...s,
        series: [{
          data: [
            Number(response.commissions?.own_total || 0),
            Number(response.commissions?.affiliates_total || 0),
          ],
        }],
      }));

      const chart3Series = [
        {
          name: 'Nivel Inferior',
          data: []
        },
        {
          name: 'Nivel Medio',
          data: []
        },
        {
          name: 'Nivel Superior',
          data: []
        }
      ];

      [
        [response.expenses.series.parts, colors.orange, 'Repuestos'],
        [response.expenses.series.categories, colors.red, 'Categorías'],
        [response.expenses.series.providers, colors.green, 'Proveedor'],
        [response.expenses.series.units, colors.blue, 'Unidad'],
      ].forEach(([serie, color, label], groupIdx) => {
        serie.forEach((item, itemIdx) => {
          chart3Series.at(2 - itemIdx).data.push({
            x: label,
            y: item.value,
            fillColor: color.at(itemIdx),
            meta: { name: item.label || '' },
          });
        });
      });

      setChart3(s => ({ ...s, series: chart3Series }));

      const maxDate = Math.max(...response.expenses.series.gas.map(x => Number(x.label)));
      const filledLabels = Array(maxDate).fill(0).map((_, idx) => String(idx + 1));
      const filledValues = Array(maxDate).fill(0);
      response.expenses.series.gas.forEach(x => {
        filledValues[Number(x.label) - 1] = Number(x.value || 0);
      });

      setChart4(s => ({
        ...s,
        series: [{
          data: filledValues,
        }],
        options: {
          ...s.options,
          labels: filledLabels,
        }
      }));

    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    chart1,
    chart2,
    chart3,
    chart4,
    currentMonth,
    data,
  };
}

export default Home;
